import type { HaftkhabisMove, HaftkhabisPlay, HaftkhabisView } from "@bing/engine-haftkhabis";

/*
 * WHAT A TAP MEANS AT THE هفت خبیث TABLE, READ OFF THE SERVER'S `legal` LIST AND NOTHING ELSE (the engine's
 * README: «What `legal` lists»). A Jack names a suit, a 2 names a seat, and both RIDE the play (D21):
 * the picker is the client's, cancellable back to the hand, and the server sees one atomic move.
 *
 * The list is awkward on purpose — a Jack comes once per suit, a 2 once per other seat, and at exactly
 * two cards in hand every entry comes twice, without and with `tak: true` — so this is the one place
 * that reads it. It also carries `catch` for a bot's own step: «A screen ignores it» (a human catches
 * through the side door, `{ type: "catch", target }` on the same `move` message).
 */

export type HaftkhabisSuit = HaftkhabisView["activeSuit"];

/** The suits a Jack's picker offers, in the hand's own order (♠♥♣♦, `sortHand`). */
export const SUIT_ORDER: readonly HaftkhabisSuit[] = ["spades", "hearts", "clubs", "diamonds"];

const playsOf = (legal: readonly HaftkhabisMove[]): HaftkhabisPlay[] =>
  legal.flatMap((m) => (m.type === "play" ? [m] : []));

/** Every card a tap may play right now. */
export function playableCards(legal: readonly HaftkhabisMove[]): Set<string> {
  return new Set(playsOf(legal).map((m) => m.card));
}

export const canDraw = (legal: readonly HaftkhabisMove[]): boolean =>
  legal.some((m) => m.type === "draw");
export const canPass = (legal: readonly HaftkhabisMove[]): boolean =>
  legal.some((m) => m.type === "pass");
export const canTake = (legal: readonly HaftkhabisMove[]): boolean =>
  legal.some((m) => m.type === "take");

/** May «تک کارت» be armed now? The list offers a `tak: true` play: exactly two cards, a play to make. */
export const canArmTak = (legal: readonly HaftkhabisMove[]): boolean =>
  playsOf(legal).some((m) => m.tak === true);

/** What a card asks before it can be played: nothing, a suit (a Jack), or a seat (a 2). */
export type CardChoice =
  | { readonly kind: "none" }
  | { readonly kind: "suit"; readonly suits: readonly HaftkhabisSuit[] }
  | { readonly kind: "target"; readonly targets: readonly number[] };

export function choiceFor(legal: readonly HaftkhabisMove[], card: string): CardChoice {
  const entries = playsOf(legal).filter((m) => m.card === card);
  const offered = new Set(entries.flatMap((m) => (m.suit === undefined ? [] : [m.suit])));
  if (offered.size > 1) return { kind: "suit", suits: SUIT_ORDER.filter((s) => offered.has(s)) };
  const targets = [...new Set(entries.flatMap((m) => (m.target === undefined ? [] : [m.target])))];
  if (targets.length > 1) return { kind: "target", targets: targets.sort((a, b) => a - b) };
  return { kind: "none" };
}

/** The player's answer to {@link choiceFor}, whichever was asked. */
export interface CardPick {
  readonly suit?: HaftkhabisSuit;
  readonly target?: number;
}

/**
 * The ONE legal entry for a card, its pick and the arm (D15) — or null where there is none (the card
 * left the list, a pick it never offered). `armed` is honoured only where the list offers `tak: true`.
 * The entry is sent exactly as the server listed it, so a stray field can never reach the door.
 */
export function playMove(
  legal: readonly HaftkhabisMove[],
  card: string,
  pick: CardPick,
  armed: boolean,
): HaftkhabisPlay | null {
  const entries = playsOf(legal).filter(
    (m) =>
      m.card === card &&
      (pick.suit === undefined || m.suit === pick.suit) &&
      (pick.target === undefined || m.target === pick.target),
  );
  const carried = armed ? entries.find((m) => m.tak === true) : undefined;
  return carried ?? entries.find((m) => m.tak !== true) ?? null;
}
